import axiosInstance from './axiosInstance';
import Axios, { AxiosRequestConfig, CancelToken, RawAxiosRequestHeaders } from 'axios';
import { IsVerbose } from './Console';
import { ContentFolder } from '../interfaces/ContentFolder';
import { ContentItem } from '../interfaces/ContentItem';
import { CustomDataDefItem } from '../interfaces/CustomDataDefItem';
import { CustomDataItem } from '../interfaces/CustomDataItem';
import { DomainContent } from '../interfaces/DomainContent';
import { EmailSignature } from '../interfaces/EmailSignature';
import { EmbeddedPictureItem } from '../interfaces/EmbeddedPictureItem';
import { ExtensionItem } from '../interfaces/ExtensionItem';
import { FileInfo } from '../interfaces/FileInfo';
import { FunctionItem } from '../interfaces/FunctionItem';
import { GroupInfo } from '../interfaces/GroupInfo';
import { IconListItem } from '../interfaces/OldIconListItem';
import { ManifestItem } from '../interfaces/ManifestItem';
import { examplePictureList, PictureListItem } from '../interfaces/PictureListItem';
import { emptyPictureBankDefs, PictureBankDefs, PictureBankPagedImages, PictureBankProfileThumb, PictureBankVersion } from '../interfaces/PictureBank';
import { ExampleLayoutList, PictureSrcLib } from '../interfaces/PictureSrcLib';
import { PresentationListItem } from '../interfaces/PresentationListItem';
import { SlideListItem } from '../interfaces/SlideListItem';
import { emptySlideDefs, SlideDefs } from '../interfaces/SlideCategories';
import { StatisticMonthLogInItem, StatisticSlideItem, StatisticTemplatesItem } from '../interfaces/Statisticd';
import { TemplateApiHeaders } from '../interfaces/TemplateApiHeaders';
import { SignatureGroupSelection, WordContentGroupSelection, OutlookContentGroupSelection, IconGroupSelection, SlideGroupSelection } from '../interfaces/GroupSelectionTypes';
import { TemplateFolderItem } from '../interfaces/TemplateFolderItem';
import { TemplateListItem } from '../interfaces/TemplateListItem';
import { UserAdminItem } from '../interfaces/UserAdminItem';
import { Excel2UserItem } from '../interfaces/Excel2UserItem';
import { LogInItem } from '../interfaces/LogInItem';
import { DomainItem } from '../interfaces/DomainItem';
import SettingsCompany, { UserInfoItem } from './SettingsCompany';
import { GroupID } from '../interfaces/GroupID';
import { adminApiUris, apiUris } from './SettingsMisc';
import { NewsItem } from '../interfaces/NewsItem';
import { XbrlTaggingListItem, XbrlTagging, GeneralTaxonomyFolder, DesktopAppFileInfo, DesktopAppDownloadLogEntry, DesktopAppVersionStatus, EsefDomainInfo, EsefBackupItem } from '../interfaces/EsefTaxonomyItem';
import { RedirectItem } from '../interfaces/RedirectItem';
import { GroupedData } from '../interfaces/GroupedData';
import { CustomDataPlainListItem } from '../interfaces/CustomDataPlainList';
import { SectionsDocument } from '../interfaces/SectionsDocument';
import { AcademyItem, AcademyLearningLevel, AcademyLevel, AcademyPlatform } from '../interfaces/AcademyItem';
import { AcademyPlatformItems } from '../interfaces/AcademyPlatform';
import SettingsShort from '../interfaces/SettingsShort';
import { UserDataGroup } from '../interfaces/UserDataGroup';
import { UserGroup } from '../interfaces/UserGroup';
import { EmailSignatureGroup } from '../interfaces/EmailSignatureGroup';
import { IconLibrary } from '../interfaces/IconLibrary';
import { IconItem } from '../interfaces/IconItem';
import { ContentType } from '../interfaces/ContentType';
import { OutlookContentsItem } from '../interfaces/OutlookContentsItem';
import { Excel2WebReportItem } from '../interfaces/Excel2WebReportItem';
import { ReportDataItem } from '../widget/interfaces/ReportDataItem';
import { ReportStructureItem } from '../widget/interfaces/ReportStructureItem';
import { AIContext, AIFeedback, AILanguage, AISource, withAISource } from '../interfaces/AIContext';

//Settings
export async function GetAdminSettings(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<SettingsCompany> {
	let settings: SettingsCompany = await GetData<SettingsCompany>(`${apiUris(api)}admin_products`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || {} as SettingsCompany;
	return settings;
}

export async function GetAllExtensions(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<ExtensionItem[]> {
	let extensions: ExtensionItem[] = await GetData<ExtensionItem[]>(`${apiUris(api)}admin_products?AllExtensions=True`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || [];
	return extensions;
}

export async function GetAvailableExtensions(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<ExtensionItem[]> {
	let extensions: ExtensionItem[] = await GetData<ExtensionItem[]>(`${apiUris(api)}admin_products?AvailableExtensions=True`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || [];
	return extensions;
}

export async function GetAllFunctions(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<FunctionItem[]> {
	let functions: FunctionItem[] = await GetData<FunctionItem[]>(`${apiUris(api)}admin_products?AllFunctions=True`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || [];
	return functions;
}

export async function GetAvailableFunctions(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<FunctionItem[]> {
	let functions: FunctionItem[] = await GetData<FunctionItem[]>(`${apiUris(api)}admin_products?AvailableFunctions=True`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || [];
	return functions;
}

export async function GetManifestItems(accessToken: string, domain: string, api: string, outlookManifest: boolean, cancelToken?: CancelToken): Promise<ManifestItem[]> {
	let manifestitems: ManifestItem[] = await GetData<ManifestItem[]>(`${apiUris(api)}admin_products?ManifestItems=True`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'IS-OUTLOOK-MANIFEST': outlookManifest ? 'true' : 'false' }, true, cancelToken!) || [];
	return manifestitems;
}

export async function GetManifestFile(accessToken: string, domain: string, api: string, includeExcel: boolean, includePowerPoint: boolean, includeWord: boolean, updateVersion: boolean, cancelToken?: CancelToken): Promise<string> {
	let manifestfile: string = await GetData<string>(`${apiUris(api)}admin_products?ManifestFile=True&excel=${includeExcel}&powerpoint=${includePowerPoint}&word=${includeWord}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'UPDATE-VERSION': updateVersion ? 'true' : 'false' }, true, cancelToken!) || '';
	return manifestfile;
}

export async function GetOutlookManifestFile(accessToken: string, domain: string, api: string, updateVersion: boolean, updateOutlookClassic: boolean, cancelToken?: CancelToken): Promise<string> {
	let manifestfile: string = await GetData<string>(`${apiUris(api)}admin_products?ManifestFile=True&outlook=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'UPDATE-VERSION': updateVersion ? 'true' : 'false', 'OUTLOOK-CLASSIC': updateOutlookClassic ? 'true' : 'false' }, true, cancelToken!) || '';
	return manifestfile;
}

export async function GetExcelManifestFile(accessToken: string, domain: string, api: string, includeExcel: boolean, includePowerPoint: boolean, includeWord: boolean, cancelToken?: CancelToken): Promise<string> {
	let manifestfile: string = await GetData<string>(`${apiUris(api)}admin_products?ManifestFile=True&excel2=true&excel=${includeExcel}&powerpoint=${includePowerPoint}&word=${includeWord}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || '';
	return manifestfile;
}

// export async function GetSettingsUpdate(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<boolean> {
// 	// let canUpdateString: string = await GetData(`${apiUris(api)}admin_products?CheckUpdate=True`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, false, cancelToken!) || false;
// 	let canUpdate: boolean = await GetData(`${apiUris(api)}admin_products?CheckUpdate=True`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, false, cancelToken!) === 'true' || false;
// 	return canUpdate;
// }


//Statistics
export async function GetLoginStatistics(accessToken: string, domain: string, api: string, isOutlook: boolean, cancelToken?: CancelToken): Promise<StatisticMonthLogInItem[]> {
	const headers = { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'IS-OUTLOOK-SIGN': isOutlook ? 'TRUE' : 'FALSE' }
	return await GetData<StatisticMonthLogInItem[]>(`${apiUris(api)}admin_statistics`, headers, true, cancelToken!) || [];
}

// Excel2 add-in unique logins ("Stat Excel2 LogIn" files), selected server-side by IS-EXCEL2.
export async function GetExcel2LoginStatistics(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<StatisticMonthLogInItem[]> {
	const headers = { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'IS-EXCEL2': 'TRUE' }
	return await GetData<StatisticMonthLogInItem[]>(`${apiUris(api)}admin_statistics`, headers, true, cancelToken!) || [];
}

export async function GetSlidesStatistics(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<StatisticSlideItem[]> {
	return await GetData<StatisticSlideItem[]>(`${apiUris(api)}admin_statistics?slides=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || [];
}

export async function GetTemplatesStatistics(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<StatisticTemplatesItem[]> {
	return await GetData<StatisticTemplatesItem[]>(`${apiUris(api)}admin_statistics?templates=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || [];
}

//New Templates
export async function GetTemplatesList(accessToken: string, domain: string, api: string, headers: TemplateApiHeaders, cancelToken?: CancelToken): Promise<TemplateListItem[]> {
	return GetData<TemplateListItem[]>(`${apiUris(api)}admin_templates`,
		{
			'Authorization': 'Bearer ' + accessToken,
			'Domain': domain,
			'API': api,
			'TEMPLATES-VERSION': headers.templateVersion,
			'TEMPLATES-TYPE': headers.templateType,
			'TEMPLATES-ENVIRON': headers.templateEnviron,
			'MY-GROUPS': headers.myGroups,
			'GROUP-SELECTION': headers.groupSelection,
			'SHOW-PATH': headers.showPath!,
			...(headers.includeDocProps && { 'INCLUDE-DOCPROPS': 'true' }),
			...(headers.includeStat && { 'INCLUDE-STAT': 'true' }),
		}, true, cancelToken!) || []
}

export async function GetTemplatesFolders(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<TemplateFolderItem[]> {
	return GetData<TemplateFolderItem[]>(`${apiUris(api)}admin_templates?folder=true`, {
		'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api
	}, true, cancelToken!) || []
}

export async function GetTemplateId(accessToken: string, domain: string, api: string, id: string, headers: TemplateApiHeaders, cancelToken?: CancelToken): Promise<string> {
	return GetData<string>(`${apiUris(api)}admin_templates/${id}`,
		{
			'Authorization': 'Bearer ' + accessToken,
			'Domain': domain,
			'API': api,
			'TEMPLATES-VERSION': headers.templateVersion,
			'TEMPLATES-TYPE': headers.templateType,
			'TEMPLATES-ENVIRON': headers.templateEnviron,
			'MY-GROUPS': headers.myGroups,
			'GROUP-SELECTION': headers.groupSelection,
		}, false, cancelToken!)
}

export async function IsTemplateAdmin(accessToken: string, domain: string, api: string, hashedUser: string, cancelToken?: CancelToken): Promise<FunctionItem[]> {
	return await GetData<FunctionItem[]>(`${apiUris(api)}admin_templates?CheckAdmin=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'MY-USER': hashedUser }, true, cancelToken!) || []
}


//Icons
export async function GetIconsList(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', myGroups?: string, groupSelection?: IconGroupSelection, cancelToken?: CancelToken): Promise<IconListItem[]> {
	return GetData<IconListItem[]>(`${apiUris(api)}admin_icons`,
		{
			'Authorization': 'Bearer ' + accessToken,
			'Domain': domain,
			'API': api,
			'TEMPLATES-VERSION': templateVersion,
			'MY-GROUPS': myGroups!,
			'GROUP-SELECTION': groupSelection!,
		}, true, cancelToken!) || []
}

export async function GetIconsFileList(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<FileInfo[]> {
	return await GetData<FileInfo[]>(`${apiUris(api)}admin_icons?fileList=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion }, true, cancelToken!) || []
}

export async function GetIconsFile(accessToken: string, domain: string, api: string, filename: string, templateVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<string> {
	return GetData<string>(`${apiUris(api)}admin_icons?fileName=${encodeURIComponent(filename)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion }, false, cancelToken!)
}

export async function GetIconsUpdate(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<boolean> {
	return GetData<string>(`${apiUris(api)}admin_icons?CheckUpdate=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, false, cancelToken!)
		.then((value) => {
			return value === 'true'
		})
}

//NewIcons
export async function GetIconLibrariesList(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', myGroups?: string, cancelToken?: CancelToken): Promise<IconLibrary[]> {
	return GetData<IconLibrary[]>(`${apiUris(api)}admin_icons?library=true`,
		{
			'Authorization': 'Bearer ' + accessToken,
			'Domain': domain,
			'API': api,
			'TEMPLATES-VERSION': templateVersion,
			'MY-GROUPS': myGroups!,
		}, true, cancelToken!) || []
}

export async function GetIconLibraryById(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', id: string, myGroups?: string, groupSelection?: IconGroupSelection, cancelToken?: CancelToken): Promise<IconItem[]> {
	return GetData<IconItem[]>(`${apiUris(api)}admin_icons?library=true&libraryId=${encodeURIComponent(id)}`,
		{
			'Authorization': 'Bearer ' + accessToken,
			'Domain': domain,
			'API': api,
			'TEMPLATES-VERSION': templateVersion,
			'MY-GROUPS': myGroups!,
			'GROUP-SELECTION': groupSelection!,
		}, true, cancelToken!) || []
}

export async function GetNewIconsUpdate(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<boolean> {
	return GetData<string>(`${apiUris(api)}admin_icons?checkLibIsUpdated=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion, }, false, cancelToken!)
		.then((value) => {
			return value === 'true'
		})
}

//Slides
export async function GetSlidesList(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', myGroups: GroupID[], groupSelection: SlideGroupSelection, presentationContent: string, cancelToken?: CancelToken, includeStat?: boolean): Promise<[SlideListItem[], number]> {
	return GetDataAndStatus<SlideListItem[]>(`${apiUris(api)}admin_slides`,
		{
			'PRESENTATION-CONTENT': presentationContent,
			'Authorization': 'Bearer ' + accessToken,
			'Domain': domain,
			'API': api,
			'TEMPLATES-VERSION': templateVersion,
			'MY-GROUPS': JSON.stringify(myGroups),
			'GROUP-SELECTION': groupSelection,
			...(includeStat && { 'INCLUDE-STAT': 'true' }),
		}, true, cancelToken!)
}

export async function GetPresentationList(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', myGroups: GroupID[], groupSelection: SlideGroupSelection, cancelToken?: CancelToken): Promise<[PresentationListItem[], number]> {
	return GetDataAndStatus<PresentationListItem[]>(`${apiUris(api)}admin_slides`,
		{
			'PRESENTATION-CONTENT': 'ALL-PRESENTATIONS',
			'Authorization': 'Bearer ' + accessToken,
			'Domain': domain,
			'API': api,
			'TEMPLATES-VERSION': templateVersion,
			'MY-GROUPS': JSON.stringify(myGroups),
			'GROUP-SELECTION': groupSelection,
		}, true, cancelToken!)
}

export async function GetSlidesPresentation(accessToken: string, domain: string, api: string, filename: string, templateVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<[string, number]> {
	return GetDataAndStatus<string>(`${apiUris(api)}admin_slides?fileName=${encodeURIComponent(filename)}`,
		{
			'Authorization': 'Bearer ' + accessToken,
			'Domain': domain,
			'API': api,
			'TEMPLATES-VERSION': templateVersion,
		}, false, cancelToken!)
}

// Slide categories. Test defs are the editing source; Pub is what the add-in sees.
export async function GetSlideDefs(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<SlideDefs> {
	const defs = await GetData<SlideDefs>(`${apiUris(api)}Admin_Slides/categories/getDefs`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion }, true, cancelToken!);
	return defs && Array.isArray(defs.categories) ? defs : emptySlideDefs;
}

//Pictures
export async function GetPictureLayoutList(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<PictureSrcLib[]> {
	return await GetData<PictureSrcLib[]>(`${apiUris(api)}admin_pictures?imgSrcList=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || ExampleLayoutList
}

// export async function GetPicturesByLayout(accessToken: string, layout: string, cancelToken?: CancelToken): Promise<PictureListItem[]> {
// 	return await GetData(`${apiUris(api)}admin_pictures?imgSrc=${layout}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || examplePictureList
// }

export async function GetPicturesByPath(accessToken: string, domain: string, api: string, path: string, cancelToken?: CancelToken): Promise<PictureListItem[]> {
	return await GetData<PictureListItem[]>(`${apiUris(api)}admin_pictures?path=${encodeURIComponent(path)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || examplePictureList
}

export async function GetPictureSizes(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<PictureSrcLib[]> {
	return await GetData<PictureSrcLib[]>(`${apiUris(api)}admin_pictures?SrcLibDef=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || ExampleLayoutList
}

export async function GetPictureCount(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<number> {
	return await GetData<number>(`${apiUris(api)}admin_pictures?imgCount=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || 0
}

export async function GetPicture(accessToken: string, domain: string, api: string, id: string, cancelToken?: CancelToken, previewOption?: string): Promise<string> {
	try {
		return JSON.parse(await GetData<string>(`${apiUris(api)}admin_pictures/${id}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, ...(previewOption && { 'PREVIEW': previewOption! }) }, false, cancelToken!))
	} catch (error) {
		if (Axios.isCancel(error)) { throw error; }
		return ''
	}
}

//PictureBank
export async function GetPictureBankDefs(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<PictureBankDefs> {
	return await GetData<PictureBankDefs>(`${apiUris(api)}AdminPictureBank/getDefs?getBankDefs=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || emptyPictureBankDefs;
}

export async function GetPictureBankImages(accessToken: string, domain: string, api: string, version: PictureBankVersion, libraryID?: string, pageOffset?: number, pageSize?: number, cancelToken?: CancelToken): Promise<PictureBankPagedImages> {
	const url = libraryID
		? `${apiUris(api)}AdminPictureBank/getImages?getAll=false&libraryID=${encodeURIComponent(libraryID)}`
		: `${apiUris(api)}AdminPictureBank/getImages?getAll=true`;
	const headers: Record<string, string> = { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': version };
	if (typeof pageOffset === 'number') headers['PAGE-OFFSET'] = String(pageOffset);
	if (typeof pageSize === 'number') headers['PAGE-SIZE'] = String(pageSize);
	const result = await GetData<PictureBankPagedImages>(url, headers, true, cancelToken!);
	return result || { totalCount: 0, items: [] };
}

export async function GetPictureBankImageThumbnails(accessToken: string, domain: string, api: string, profileID: string, version: PictureBankVersion, cancelToken?: CancelToken): Promise<PictureBankProfileThumb[]> {
	return await GetData<PictureBankProfileThumb[]>(`${apiUris(api)}AdminPictureBank/getImageThumbnails?profileID=${encodeURIComponent(profileID)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': version }, true, cancelToken!) || [];
}

/** Sentinel returned by GetPictureBankImageFile when the backend rejects the profile for this image (HTTP 405). */
export const PICTURE_BANK_PROFILE_UNAVAILABLE = '__PROFILE_UNAVAILABLE__';

export async function GetPictureBankImageFile(accessToken: string, domain: string, api: string, imageID: string, version: PictureBankVersion, previewOption?: string, cancelToken?: CancelToken): Promise<string> {
	try {
		return JSON.parse(await GetData<string>(`${apiUris(api)}AdminPictureBank/getImageFile?imageID=${encodeURIComponent(imageID)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': version, ...(previewOption && { 'PROFILE-ID': previewOption }) }, false, cancelToken!));
	} catch (error) {
		if (Axios.isCancel(error)) { throw error; }
		// 405 = profile not available for this image (Profilmodell v2). Signal it distinctly so the UI can explain.
		if (Axios.isAxiosError(error) && error.response?.status === 405) { return PICTURE_BANK_PROFILE_UNAVAILABLE; }
		return '';
	}
}

//CustomData
export async function GetCustomData(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<CustomDataItem[]> {
	return await GetData<CustomDataItem[]>(`${apiUris(api)}admin_customdata`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

export async function GetCustomDataById(accessToken: string, domain: string, api: string, dataId: string, cancelToken?: CancelToken): Promise<CustomDataItem> {
	return GetData<CustomDataItem>(`${apiUris(api)}admin_customdata?DataID=${encodeURIComponent(dataId)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!)
}

export async function GetCustomDataDef(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<CustomDataDefItem> {
	return GetData<CustomDataDefItem>(`${apiUris(api)}admin_customdata?def=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!)
}

export async function GetCustomPlainList(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<CustomDataPlainListItem[]> {
	return await GetData<CustomDataPlainListItem[]>(`${apiUris(api)}admin_customdata?plaindatalist=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'ONLY-FIRST': 'TRUE' }, true, cancelToken!) || []
}

//Sections
export async function GetSectionsList(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', myGroups?: string, cancelToken?: CancelToken): Promise<SectionsDocument[]> {
	return await GetData<SectionsDocument[]>(`${apiUris(api)}admin_docbuilder?list=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion, 'MY-GROUPS': myGroups! }, true, cancelToken!) || []
}

export async function GetSectionsDoc(accessToken: string, domain: string, api: string, filename: string, templateVersion: 'TEST' | 'PUB', myGroups?: string, cancelToken?: CancelToken): Promise<string> {
	return GetData<string>(`${apiUris(api)}admin_docbuilder?filename=${encodeURIComponent(filename)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion, 'MY-GROUPS': myGroups! }, false, cancelToken!)
}

//Emailsignatures
export async function GetEmailSignatures(accessToken: string, domain: string, api: string, signatureVersion: string, myGroups?: string, groupSelection?: SignatureGroupSelection, cancelToken?: CancelToken): Promise<EmailSignature[]> {
	return await GetData<EmailSignature[]>(`${apiUris(api)}admin_emailsignatures`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'ALL-SIGNATURES': 'TRUE', 'EMAIL-SIGNATURE-VERSION': signatureVersion, 'MY-GROUPS': myGroups!, 'GROUP-SELECTION': groupSelection! }, true, cancelToken!) || []
}

export async function GetEmailSignaturesByGroups(accessToken: string, domain: string, api: string, signatureVersion: string, myGroups?: string, groupSelection?: SignatureGroupSelection, cancelToken?: CancelToken): Promise<EmailSignatureGroup[]> {
	return await GetData<EmailSignatureGroup[]>(`${apiUris(api)}admin_emailsignatures?bygroups=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'ALL-SIGNATURES': 'TRUE', 'EMAIL-SIGNATURE-VERSION': signatureVersion, 'MY-GROUPS': myGroups!, 'GROUP-SELECTION': groupSelection! }, true, cancelToken!) || []
}

export async function GetEmailSignaturePictures(accessToken: string, domain: string, api: string, signatureVersion: string, cancelToken?: CancelToken): Promise<EmbeddedPictureItem[]> {
	return await GetData<EmbeddedPictureItem[]>(`${apiUris(api)}admin_emailsignatures?pictures=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'EMAIL-SIGNATURE-VERSION': signatureVersion }, true, cancelToken!) || []
}

//Word Content
export async function IsWordContentAdmin(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', hashedUser: string, cancelToken?: CancelToken): Promise<boolean> {
	return GetData<boolean>(`${apiUris(api)}admin_wordcontents?CheckAdmin=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion, 'MY-USER': hashedUser }, true, cancelToken!)
}

export async function GetWordContentFolders(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', hashedUser: string, myGroups?: string, groupSelection?: WordContentGroupSelection, cancelToken?: CancelToken): Promise<ContentFolder[]> {
	return await GetData<ContentFolder[]>(`${apiUris(api)}admin_wordcontents?Folder=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion, 'MY-USER': hashedUser, 'MY-GROUPS': myGroups!, 'GROUP-SELECTION': groupSelection! }, true, cancelToken!) || []
}

export async function GetWordContent(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', search: string, myGroups?: string, groupSelection?: WordContentGroupSelection, cancelToken?: CancelToken): Promise<ContentItem[]> {
	return await GetData<ContentItem[]>(`${apiUris(api)}admin_wordcontents`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion, 'MY-SEARCH': search, 'MY-GROUPS': myGroups!, 'GROUP-SELECTION': groupSelection! }, true, cancelToken!) || []
}

export async function GetWordContentById(accessToken: string, domain: string, api: string, contentId: string, templateVersion: 'TEST' | 'PUB', myGroups?: string, groupSelection?: WordContentGroupSelection, cancelToken?: CancelToken): Promise<ContentItem> {
	return GetData<ContentItem>(`${apiUris(api)}admin_wordcontents?ContentID=${encodeURIComponent(contentId)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion, 'MY-GROUPS': myGroups!, 'GROUP-SELECTION': groupSelection! }, true, cancelToken!)
}

export async function GetWordContentTypes(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<ContentType[]> {
	return await GetData<ContentType[]>(`${apiUris(api)}admin_wordcontents?contentTypes=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion }, true, cancelToken!) || []
}

export async function GetWordContentColors(accessToken: string, domain: string, api: string, templateVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<string[]> {
	return await GetData<string[]>(`${apiUris(api)}admin_wordcontents?iconColors=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'TEMPLATES-VERSION': templateVersion }, true, cancelToken!) || []
}

//Outlook Content
export async function GetOutlookContents(accessToken: string, domain: string, api: string, contentVersion: 'TEST' | 'PUB', myGroups?: string, groupSelection?: OutlookContentGroupSelection, cancelToken?: CancelToken): Promise<OutlookContentsItem[]> {
	return await GetData<OutlookContentsItem[]>(`${apiUris(api)}admin_outlookcontents`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'OUTLOOK-CONTENT-VERSION': contentVersion, 'MY-GROUPS': myGroups!, 'GROUP-SELECTION': groupSelection! }, true, cancelToken!) || []
}

export async function GetOutlookContent(accessToken: string, domain: string, api: string, contentId: string, contentVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<OutlookContentsItem> {
	return GetData<OutlookContentsItem>(`${apiUris(api)}admin_outlookcontents?contentID=${encodeURIComponent(contentId)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'OUTLOOK-CONTENT-VERSION': contentVersion }, true, cancelToken!)
}

export async function GetOutlookContentFolders(accessToken: string, domain: string, api: string, contentVersion: 'TEST' | 'PUB', myGroups?: string, groupSelection?: OutlookContentGroupSelection, cancelToken?: CancelToken): Promise<ContentFolder[]> {
	return await GetData<ContentFolder[]>(`${apiUris(api)}admin_outlookcontents?Folder=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'OUTLOOK-CONTENT-VERSION': contentVersion, 'MY-GROUPS': myGroups!, 'GROUP-SELECTION': groupSelection! }, true, cancelToken!) || []
}

export async function GetOutlookContentTypes(accessToken: string, domain: string, api: string, contentVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<ContentType[]> {
	return await GetData<ContentType[]>(`${apiUris(api)}admin_outlookcontents?contentTypes=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'OUTLOOK-CONTENT-VERSION': contentVersion }, true, cancelToken!) || []
}

export async function GetOutlookContentColors(accessToken: string, domain: string, api: string, contentVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<string[]> {
	return await GetData<string[]>(`${apiUris(api)}admin_outlookcontents?iconColors=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'OUTLOOK-CONTENT-VERSION': contentVersion }, true, cancelToken!) || []
}

export async function GetOutlookContentPermission(accessToken: string, domain: string, api: string, contentVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<boolean> {
	return GetData<boolean>(`${apiUris(api)}admin_outlookcontents?CheckAdmin=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'OUTLOOK-CONTENT-VERSION': contentVersion }, true, cancelToken!)
}

export async function GetOutlookContentPictures(accessToken: string, domain: string, api: string, contentVersion: 'TEST' | 'PUB', cancelToken?: CancelToken): Promise<EmbeddedPictureItem[]> {
	return await GetData<EmbeddedPictureItem[]>(`${apiUris(api)}admin_outlookcontents?Pictures=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, 'OUTLOOK-CONTENT-VERSION': contentVersion }, true, cancelToken!) || []
}

//Groups
export async function GetGroups(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<GroupInfo[]> {
	return await GetData<GroupInfo[]>(`${apiUris(api)}admin_groups`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

//User Data Groups
export async function GetUserDataGroups(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<UserDataGroup[]> {
	return await GetData<UserDataGroup[]>(`${apiUris(api)}admin_groups?userdata=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

//Users
export async function GetUser(accessToken: string, checkDomains: boolean, cancelToken?: CancelToken): Promise<UserAdminItem> {
	const [data, status] = await GetDataAndStatus<UserAdminItem>(`${adminApiUris()}admin_users`, { 'Authorization': 'Bearer ' + accessToken, 'CHECK-DOMAINS': checkDomains ? 'true' : 'false' }, true, cancelToken!)
	if (status === 404) {
		const error = new Error('Your account is missing.');
		(error as any).status = 404;
		throw error;
	}
	if (status === 0 || status >= 400) {
		const error = new Error('Server error occurred. Please try again later or contact support.');
		(error as any).status = status;
		throw error;
	}
	return data;
}

// Unified sign-in: returns both the users.json entry (User) and the Excel2Users.json
// entry (Excel2User); a presence in either is enough to sign in. Replaces GetUser for
// the login flow so Excel2/ESEF-only users can reach the portal. Same 404/error contract.
export async function GetLogIn(accessToken: string, checkDomains: boolean, cancelToken?: CancelToken): Promise<LogInItem> {
	const [data, status] = await GetDataAndStatus<LogInItem>(`${adminApiUris()}admin_login`, { 'Authorization': 'Bearer ' + accessToken, 'CHECK-DOMAINS': checkDomains ? 'true' : 'false' }, true, cancelToken!)
	if (status === 404) {
		const error = new Error('Your account is missing.');
		(error as any).status = 404;
		throw error;
	}
	if (status === 0 || status >= 400) {
		const error = new Error('Server error occurred. Please try again later or contact support.');
		(error as any).status = status;
		throw error;
	}
	return data;
}

export async function GetAccountsCSV(accessToken: string, api: string, cancelToken?: CancelToken): Promise<string> {
	const data: string = await GetData<string>(`${adminApiUris()}admin_users?AllAccounts=true`, { 'Authorization': 'Bearer ' + accessToken, 'API': api, 'GET-AS-CSV': true }, true, cancelToken!) || ''
	return data
}

export async function GetUserDataGroupsList(accessToken: string, api: string, domain: string, cancelToken?: CancelToken): Promise<UserDataGroup[]> {
	const data: UserDataGroup[] = await GetData<UserDataGroup[]>(`${adminApiUris()}admin_users?groups=true&api=${encodeURIComponent(api)}&domain=${encodeURIComponent(domain)}`, { 'Authorization': 'Bearer ' + accessToken }, true, cancelToken!) || []
	return data
}

export async function GetUserDataGroupsByDomain(accessToken: string, api: string, domain: string, cancelToken?: CancelToken): Promise<UserGroup[]> {
	const data: UserGroup[] = await GetData<UserGroup[]>(`${adminApiUris()}admin_users?groups=true&bydomain=true&api=${encodeURIComponent(api)}&domain=${encodeURIComponent(domain)}`, { 'Authorization': 'Bearer ' + accessToken }, true, cancelToken!) || []
	return data
}

export async function GetUserDataGroupsByUser(accessToken: string, api: string, domain: string, user: string, cancelToken?: CancelToken): Promise<UserDataGroup[]> {
	const data: UserDataGroup[] = await GetData<UserDataGroup[]>(`${adminApiUris()}admin_users?groups=true&user=${encodeURIComponent(user)}&api=${encodeURIComponent(api)}&domain=${encodeURIComponent(domain)}`, { 'Authorization': 'Bearer ' + accessToken }, true, cancelToken!) || []
	return data
}

export async function GetAvailableUserDataGroups(accessToken: string, api: string, domain: string, onlyMyGroups: boolean, cancelToken?: CancelToken): Promise<UserDataGroup[]> {
	const data: UserDataGroup[] = await GetData<UserDataGroup[]>(`${adminApiUris()}admin_users?availablegroups=true&api=${encodeURIComponent(api)}&domain=${encodeURIComponent(domain)}`, { 'Authorization': 'Bearer ' + accessToken, 'ONLY-MY-GROUPS': onlyMyGroups ? 'true' : 'false' }, true, cancelToken!) || []
	return data
}

// export async function VerifyUser(accesstoken: string, tenantDomains: string, cancelToken?: CancelToken): Promise<boolean> {
// 	return GetData(`${adminApiUris()}users?VerifyUser=true`, { 'Authorization': `Bearer ${accesstoken}`, 'MY-DOMAINS': tenantDomains }, false, cancelToken!)
// }

export async function GetAllUsers(accessToken: string, allDomains?: boolean, cancelToken?: CancelToken): Promise<UserAdminItem[]> {
	return await GetData<UserAdminItem[]>(`${adminApiUris()}admin_users?allusers=true`, { 'Authorization': 'Bearer ' + accessToken, 'ALL-DOMAINS': allDomains ? 'TRUE' : '' }, true, cancelToken!) || []
}

// Excel2/ESEF users, stored apart from ordinary users (Excel2Users.json) and gated
// server-side by isAdmin OR isExcel2Admin. An isExcel2Admin only sees users within
// their own domains/APIs; an isAdmin sees everyone.
export async function GetAllExcel2Users(accessToken: string, cancelToken?: CancelToken): Promise<Excel2UserItem[]> {
	return await GetData<Excel2UserItem[]>(`${adminApiUris()}admin_excel2users?AllUsers=true`, { 'Authorization': 'Bearer ' + accessToken }, true, cancelToken!) || []
}

// Domains that can be picked for change tenant on the Excel2 track: those carrying an
// Excel2 extension, already filtered and scoped server-side (isAdmin sees all for the
// API, an isExcel2Admin only their own). Gated by isAdmin OR isExcel2Admin.
export async function GetExcel2Domains(accessToken: string, api: string, cancelToken?: CancelToken): Promise<DomainItem[]> {
	return await GetData<DomainItem[]>(`${adminApiUris()}admin_excel2users?Excel2Domains=true&API=${api}`, { 'Authorization': 'Bearer ' + accessToken }, true, cancelToken!) || []
}

export async function GetUserIsAdmin(accessToken: string, cancelToken?: CancelToken): Promise<boolean> {
	return GetData<boolean>(`${adminApiUris()}admin_users?isAdmin=true`, { 'Authorization': 'Bearer ' + accessToken }, false, cancelToken!)
}

export async function GetDomainContent(accessToken: string, api: string, cancelToken?: CancelToken): Promise<DomainContent[]> {
	const data: DomainContent[] = await GetData<DomainContent[]>(`${adminApiUris()}admin_domains?api=${api}`, { 'Authorization': 'Bearer ' + accessToken }, true, cancelToken!) || []
	return data
}

export async function GetDomainExtensions(accessToken: string, api: string, cancelToken?: CancelToken): Promise<SettingsShort[]> {
	const data: SettingsShort[] = await GetData<SettingsShort[]>(`${adminApiUris()}admin_domains?AllProducts=true`, { 'Authorization': 'Bearer ' + accessToken, 'API': api }, true, cancelToken!) || []
	return data
}

export async function GetDomainsCSV(accessToken: string, api: string, cancelToken?: CancelToken): Promise<string> {
	const data: string = await GetData<string>(`${adminApiUris()}admin_domains?AllProducts=true`, { 'Authorization': 'Bearer ' + accessToken, 'API': api, 'GET-AS-CSV': true }, true, cancelToken!) || ''
	return data
}

//UserData
export async function GetUserData(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<UserInfoItem[]> {
	return await GetData<UserInfoItem[]>(`${apiUris(api)}admin_userdata`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, }, true, cancelToken!) || []
}

export async function GetAllUserData(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<UserInfoItem[]> {
	return await GetData<UserInfoItem[]>(`${apiUris(api)}admin_userdata?ad=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, }, true, cancelToken!) || []
}

//GroupedData
export async function GetAllCombinedData(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<GroupedData[]> {
	return await GetData<GroupedData[]>(`${apiUris(api)}admin_userdata?groupedData=true`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, }, true, cancelToken!) || []
}

export async function GetCombinedDataById(accessToken: string, id: string, domain: string, api: string, cancelToken?: CancelToken): Promise<GroupedData> {
	return GetData<GroupedData>(`${apiUris(api)}admin_userdata?groupedData=true&id=${encodeURIComponent(id)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api, }, true, cancelToken!)
}

//Redirect
export async function GetRedirects(accessToken: string, targetDomain: string, api: string, cancelToken?: CancelToken): Promise<RedirectItem[]> {
	return await GetData<RedirectItem[]>(`${adminApiUris()}admin_domains?redirect=true&targetdomain=${encodeURIComponent(targetDomain)}`, { 'Authorization': 'Bearer ' + accessToken, 'API': api, }, true, cancelToken!) || []
}

//News
export async function GetAllNews(accessToken: string, newsVersion: 'TEST' | 'PROD', cancelToken?: CancelToken): Promise<NewsItem[]> {
	return await GetData<NewsItem[]>(`${adminApiUris()}admin_news`, { 'Authorization': 'Bearer ' + accessToken, 'NEWS-VERSION': newsVersion }, true, cancelToken!) || []
}

//Logo
export async function GetLogo(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<string> {
	try {
		return JSON.parse(await GetData<string>(`${apiUris(api)}admin_products?application=logo`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, false, cancelToken!))
	} catch (error) {
		if (Axios.isCancel(error)) { throw error; }
		return ''
	}
}

//Academy
export async function GetAllAcademyItems(accessToken: string, api: string, cancelToken?: CancelToken): Promise<AcademyItem[]> {
	return await GetData<AcademyItem[]>(`${apiUris(api)}admin_academy`, { 'Authorization': 'Bearer ' + accessToken, 'API': api }, true, cancelToken!) || []
}

export async function GetAcademyList(accessToken: string, api: string, language: string, cancelToken?: CancelToken): Promise<AcademyPlatformItems[]> {
	return await GetData<AcademyPlatformItems[]>(`${apiUris(api)}admin_academy?simple=true&language=${encodeURIComponent(language)}`, { 'Authorization': 'Bearer ' + accessToken, 'API': api }, true, cancelToken!) || []
}

export async function GetAcademyItem(accessToken: string, api: string, id: string, cancelToken?: CancelToken): Promise<AcademyItem> {
	return GetData<AcademyItem>(`${apiUris(api)}admin_academy?id=${encodeURIComponent(id)}&a=true&b=true`, { 'Authorization': 'Bearer ' + accessToken, 'API': api }, true, cancelToken!)
}

export async function GetAcademyLearningLevels(accessToken: string, api: string, cancelToken?: CancelToken): Promise<AcademyLearningLevel[]> {
	return await GetData<AcademyLearningLevel[]>(`${apiUris(api)}admin_academy?list=learninglevels`, { 'Authorization': 'Bearer ' + accessToken, 'API': api }, true, cancelToken!) || []
}

export async function GetAcademyPlatforms(accessToken: string, api: string, cancelToken?: CancelToken): Promise<AcademyPlatform[]> {
	return await GetData<AcademyPlatform[]>(`${apiUris(api)}admin_academy?list=platforms`, { 'Authorization': 'Bearer ' + accessToken, 'API': api }, true, cancelToken!) || []
}

export async function GetAcademyLevels(accessToken: string, api: string, cancelToken?: CancelToken): Promise<AcademyLevel[]> {
	return await GetData<AcademyLevel[]>(`${apiUris(api)}admin_academy?list=levels`, { 'Authorization': 'Bearer ' + accessToken, 'API': api }, true, cancelToken!) || []
}

//Tutorials
export async function GetAllTutorialsItems(accessToken: string, domain: string, api: string, tutorialVersion: string, cancelToken?: CancelToken): Promise<AcademyItem[]> {
	return GetData<AcademyItem[]>(`${apiUris(api)}admin_tutorials`, {
		'Authorization': 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TUTORIAL-VERSION': tutorialVersion
	}, true, cancelToken!) || []
}

export async function GetTutorialsList(accessToken: string, domain: string, api: string, language: string, tutorialVersion: string, cancelToken?: CancelToken): Promise<AcademyPlatformItems[]> {
	return GetData<AcademyPlatformItems[]>(`${apiUris(api)}admin_tutorials?simple=true&language=${encodeURIComponent(language)}`, {
		'Authorization': 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TUTORIAL-VERSION': tutorialVersion
	}, true, cancelToken!) || []
}

export async function GetTutorialsItem(accessToken: string, domain: string, api: string, id: string, tutorialVersion: string, cancelToken?: CancelToken): Promise<AcademyItem> {
	return GetData<AcademyItem>(`${apiUris(api)}admin_tutorials?id=${encodeURIComponent(id)}&a=true&b=true`, {
		'Authorization': 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TUTORIAL-VERSION': tutorialVersion
	}, true, cancelToken!)
}

export async function GetTutorialsLearningLevels(accessToken: string, domain: string, api: string, tutorialVersion: string, cancelToken?: CancelToken): Promise<AcademyLearningLevel[]> {
	return GetData<AcademyLearningLevel[]>(`${apiUris(api)}admin_tutorials?list=learninglevels`, {
		'Authorization': 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TUTORIAL-VERSION': tutorialVersion
	}, true, cancelToken!) || []
}

export async function GetTutorialsPlatforms(accessToken: string, domain: string, api: string, tutorialVersion: string, cancelToken?: CancelToken): Promise<AcademyPlatform[]> {
	return GetData<AcademyPlatform[]>(`${apiUris(api)}admin_tutorials?list=platforms`, {
		'Authorization': 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TUTORIAL-VERSION': tutorialVersion
	}, true, cancelToken!) || []
}

//Excel2web
export async function GetExcel2WebReports(accessToken: string, domain: string, api: string, version: string, cancelToken?: CancelToken): Promise<[Excel2WebReportItem[], number]> {
	return GetDataAndStatus<Excel2WebReportItem[]>(`${apiUris(api)}admin_excel2web`, {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TEMPLATES-VERSION': version,
	}, true, cancelToken!)
}

export async function GetExcel2WebReportById(accessToken: string, domain: string, api: string, id: string, version: string, cancelToken?: CancelToken): Promise<[Excel2WebReportItem, number]> {
	return GetDataAndStatus<Excel2WebReportItem>(`${apiUris(api)}admin_excel2web?id=${encodeURIComponent(id)}`, {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TEMPLATES-VERSION': version,
	}, true, cancelToken!)
}

export async function GetExcel2WebReportDataById(accessToken: string, domain: string, api: string, id: string, version: string, cancelToken?: CancelToken): Promise<[ReportDataItem, number]> {
	return GetDataAndStatus<ReportDataItem>(`${apiUris(api)}admin_excel2web?reportdata=true&id=${encodeURIComponent(id)}`, {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TEMPLATES-VERSION': version,
	}, true, cancelToken!)
}

export async function GetExcel2WebReportStructureById(accessToken: string, domain: string, api: string, id: string, version: string, cancelToken?: CancelToken): Promise<[ReportStructureItem, number]> {
	return GetDataAndStatus<ReportStructureItem>(`${apiUris(api)}admin_excel2web?pagestructure=true&id=${encodeURIComponent(id)}`, {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TEMPLATES-VERSION': version,
	}, true, cancelToken!)
}

//GPT
export async function GetGptToken(accessToken: string, cancelToken?: CancelToken): Promise<string> {
	return GetData<string>(`${adminApiUris()}admin_gpt`, { Authorization: 'Bearer ' + accessToken }, false, cancelToken!)
}

//AI
// source narrows the result to GLOBAL or DOMAIN. Left out, the API returns both
// (global ones only for isAdmin), each tagged with its own source.
export async function GetAIContexts(accessToken: string, domain: string, api: string, version: string, source?: AISource, cancelToken?: CancelToken): Promise<[AIContext[], number]> {
	return GetDataAndStatus<AIContext[]>(withAISource(`${apiUris(api)}AdminAI/getContexts?getAllContexts=true`, source), {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TEMPLATES-VERSION': version,
	}, true, cancelToken!)
}

export async function GetAIContextById(accessToken: string, domain: string, api: string, id: string, version: string, source?: AISource, cancelToken?: CancelToken): Promise<[AIContext, number]> {
	return GetDataAndStatus<AIContext>(withAISource(`${apiUris(api)}AdminAI/getContext?contextID=${encodeURIComponent(id)}`, source), {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
		'TEMPLATES-VERSION': version,
	}, true, cancelToken!)
}

// Languages are not TEST/PUB versioned, so no TEMPLATES-VERSION header.
export async function GetAILanguages(accessToken: string, domain: string, api: string, source?: AISource, cancelToken?: CancelToken): Promise<[AILanguage[], number]> {
	return GetDataAndStatus<AILanguage[]>(withAISource(`${apiUris(api)}AdminAI/getLanguages?getAllLanguages=true`, source), {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
	}, true, cancelToken!)
}

// AI feedback log. isAdmin only - the API answers 405 for everyone else, and 204
// when the log is empty, which GetData turns into null; hence the || [] fallback.
export async function GetAIFeedbacks(accessToken: string, domain: string, api: string, includeAcknowledged: boolean, cancelToken?: CancelToken): Promise<AIFeedback[]> {
	return await GetData<AIFeedback[]>(`${apiUris(api)}AdminAI/getFeedbacks?getAllFeedbacks=true&includeAcknowledged=${includeAcknowledged}`, {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
	}, true, cancelToken!) || []
}

export async function GetAIFeedback(accessToken: string, domain: string, api: string, feedbackId: string, cancelToken?: CancelToken): Promise<AIFeedback | null> {
	return await GetData<AIFeedback>(`${apiUris(api)}AdminAI/getFeedback?feedbackId=${encodeURIComponent(feedbackId)}`, {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
	}, true, cancelToken!)
}

// Counts files without reading them, so it is cheap enough to poll.
export async function GetAINewFeedbacks(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<[{ hasNew: boolean, newCount: number }, number]> {
	return GetDataAndStatus<{ hasNew: boolean, newCount: number }>(`${apiUris(api)}AdminAI/getNewFeedbacks?checkNewFeedbacks=true`, {
		Authorization: 'Bearer ' + accessToken,
		'Domain': domain,
		'API': api,
	}, true, cancelToken!)
}

//Graph
export async function GetFromGraph<T>(accessToken: string, endpoint: string, select?: string, isPicture?: boolean, useScopes?: string, cancelToken?: CancelToken): Promise<T> {
	const [rawData, status] = await GetDataAndStatus<string>(`${import.meta.env.VITE_API_FOR_GRAPH || ''}graph`, {
		Authorization: 'Bearer ' + accessToken,
		'ENDPOINT': endpoint,
		'SELECT': select,
		'USE-SCOPES': useScopes || 'USER.READ',
		...isPicture && { 'content-type': 'image/jpeg', }
	}, false, cancelToken!);

	if (status === 404) {
		return {} as T;
	}

	// First parse to unwrap the response
	const firstParse = JSON.parse(rawData);

	// Check if it's a photo endpoint (returns plain base64 string)
	if (endpoint.includes('/photos/') || endpoint.includes('/$value')) {
		// Backend now returns plain base64 string for images
		return firstParse as T;
	}

	// For normal Graph API responses, do the second parse
	return JSON.parse(firstParse) as T;
}



async function fetchData<T = any>(url: string, headers: RawAxiosRequestHeaders, isObject: boolean, cancelToken?: CancelToken): Promise<[T, number]> {
	let myData: T = (isObject ? null : '') as unknown as T
	let status: number = 0
	const config: AxiosRequestConfig = {
		url: url,
		method: 'GET',
		headers: headers,
		cancelToken: cancelToken!,
	}
	await axiosInstance(config)
		.then((response) => {
			if (!response) return;
			status = response.status
			if (status === 204) return;
			myData = (isObject ? response.data : JSON.stringify(response.data)) as T
		})
		.catch((requestError) => {
			if (Axios.isCancel(requestError)) { throw requestError; }
			IsVerbose() && console.log(requestError)
			status = requestError?.response?.status ?? 0
		});
	return [myData, status]
}

export async function GetData<T = any>(url: string, headers: RawAxiosRequestHeaders, isObject: boolean, cancelToken?: CancelToken): Promise<T> {
	const [data] = await fetchData<T>(url, headers, isObject, cancelToken);
	return data;
}

export async function GetDataAndStatus<T = any>(url: string, headers: RawAxiosRequestHeaders, isObject: boolean, cancelToken?: CancelToken): Promise<[T, number]> {
	return fetchData<T>(url, headers, isObject, cancelToken);
}

//ESEF Taxonomy
export async function GetEsefTaxonomies(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<XbrlTaggingListItem[]> {
	return await GetData<XbrlTaggingListItem[]>(`${apiUris(api)}Admin_ESEF`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

export async function GetEsefTaxonomy(accessToken: string, domain: string, api: string, id: string, cancelToken?: CancelToken): Promise<XbrlTagging> {
	return await GetData<XbrlTagging>(`${apiUris(api)}Admin_ESEF/${id}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || {} as XbrlTagging
}

// Export-to-domain targets. Rehngruppen admins only (isRehngruppen AND (isAdmin
// OR isExcel2Admin)) - anyone else gets 405 from the API.
export async function GetEsefDomains(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<EsefDomainInfo[]> {
	return await GetData<EsefDomainInfo[]>(`${apiUris(api)}Admin_ESEF/domains`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

// Restore points for one taxonomy, newest first. The API answers 204 when the
// taxonomy has none, which GetData turns into null - hence the empty-array
// fallback, same as the other ESEF listings.
export async function GetEsefTaxonomyBackups(accessToken: string, domain: string, api: string, id: string, cancelToken?: CancelToken): Promise<EsefBackupItem[]> {
	return await GetData<EsefBackupItem[]>(`${apiUris(api)}Admin_ESEF/backups?id=${encodeURIComponent(id)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

export async function GetEsefGeneralTaxonomies(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<GeneralTaxonomyFolder[]> {
	return await GetData<GeneralTaxonomyFolder[]>(`${apiUris(api)}Admin_ESEF/general/taxonomies`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

export async function GetEsefGeneralTaxonomyFiles(accessToken: string, domain: string, api: string, name: string, cancelToken?: CancelToken): Promise<string[]> {
	return await GetData<string[]>(`${apiUris(api)}Admin_ESEF/general/taxonomies/${encodeURIComponent(name)}`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

export async function GetEsefDesktopAppFiles(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<DesktopAppFileInfo[]> {
	return await GetData<DesktopAppFileInfo[]>(`${apiUris(api)}Admin_ESEF/desktop-app`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

export async function GetEsefDesktopAppDownloadLog(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<DesktopAppDownloadLogEntry[]> {
	return await GetData<DesktopAppDownloadLogEntry[]>(`${apiUris(api)}Admin_ESEF/desktop-app/downloads`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

export async function GetEsefDesktopAppVersionStatus(accessToken: string, domain: string, api: string, cancelToken?: CancelToken): Promise<DesktopAppVersionStatus[]> {
	return await GetData<DesktopAppVersionStatus[]>(`${apiUris(api)}Admin_ESEF/desktop-app/version-status`, { 'Authorization': 'Bearer ' + accessToken, 'Domain': domain, 'API': api }, true, cancelToken!) || []
}

export async function DownloadEsefDesktopAppFile(accessToken: string, domain: string, api: string, id: string, cancelToken?: CancelToken, onDownloadProgress?: (progressEvent: any) => void): Promise<Blob | null> {
	try {
		const response = await axiosInstance({
			url: `${apiUris(api)}Admin_ESEF/desktop-app/download/${encodeURIComponent(id)}`,
			method: 'GET',
			headers: {
				'Authorization': 'Bearer ' + accessToken,
				'Domain': domain,
				'API': api,
			},
			responseType: 'blob',
			cancelToken: cancelToken!,
			onDownloadProgress,
		});
		return response.data as Blob;
	} catch (error) {
		if (Axios.isCancel(error)) {
			throw error;
		}
		console.error('Error downloading ESEF desktop app file:', error);
		return null;
	}
}